import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "~/components/buttons/Button/Button";
import DatePickerInput from "~/components/formElements/DatePickerInput";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import Input from "~/components/formElements/Input";
import SubmitButton from "~/components/formElements/SubmitButton";
import Hint from "~/components/Hint";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import { invoiceStatusId } from "~/modules/billing/api/invoiceStatus/invoiceStatusId.ts";
import { invoiceTypeId } from "~/modules/billing/api/invoiceType.ts";
import { updateInvoicePaymentDetails } from "~/modules/billing/api/processedInvoice/processedInvoiceApiDispatchers.ts";
import type { Invoice, InvoiceStatusId } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import { DEFAULT_PAYMENT_TARGET_DAYS } from "~/modules/billing/constants/billingConstants.ts";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type FormData = {
	paymentTargetDays: number;
	invoiceDeliveryDate: Date | null;
	invoicePaidAt: Date | null;
}

type Props = {
	invoiceData: Invoice;
	onCancel: () => void;
	onSuccess: () => void;
};

const workInProgressStatuses: InvoiceStatusId[] = [invoiceStatusId.Draft, invoiceStatusId.AwaitingApproval, invoiceStatusId.Approved];
const processedStatuses: InvoiceStatusId[] = [invoiceStatusId.Paid, invoiceStatusId.Reversed, invoiceStatusId.SentToClient];

const UpdateInvoicePaymentInformationForm = ({
	invoiceData,
	onCancel,
	onSuccess,
}: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");

	const currentStatusId = invoiceData.invoiceStatusId;
	const isSelfBilledInvoice = invoiceData.invoiceTypeId === invoiceTypeId.SelfBilledInvoice;
	const isWorkInProgress = workInProgressStatuses.includes(currentStatusId);
	const isAlreadyProcessed = processedStatuses.includes(currentStatusId);

	const deliveryDateDisabled = isWorkInProgress;
	const paidAtDisabled = isWorkInProgress || currentStatusId === invoiceStatusId.SentToClient;

	const schema = useMemo(() => {
		return yup.object({
			paymentTargetDays: yup
				.number()
				.required()
				.default(DEFAULT_PAYMENT_TARGET_DAYS)
				.min(1, "Zahlungsziel muss mindestens 1 Tag betragen"),
			invoiceDeliveryDate: yup
				.date()
				.nullable()
				.defined()
				.when([], {
					is: () => !isWorkInProgress,
					then: (s) => s.required("Lieferdatum ist erforderlich"),
					otherwise: (s) => s.nullable(),
				}),
			invoicePaidAt: yup
				.date()
				.nullable()
				.defined()
				.when([], {
					is: () => currentStatusId === invoiceStatusId.Paid || currentStatusId === invoiceStatusId.Reversed,
					then: (s) => s.required("Zahlungsdatum ist erforderlich").min(yup.ref("invoiceDeliveryDate"), "Das Lieferdatum muss vor dem Zahlungsdatum liegen."),
					otherwise: (s) => s.nullable(),
				}),
			/*			.test(
							"after-delivery-date",
							"Zahlungsdatum muss nach dem Lieferdatum liegen",
							function(value) {
								const { invoiceDeliveryDate } = this.parent;
								if (!value || !invoiceDeliveryDate) return true;
								return value >= invoiceDeliveryDate;
							},
						),*/
		});
	}, [isWorkInProgress, currentStatusId]);

	const defaultValues = useMemo(() => {
		const invoiceDeliveryDateString = isSelfBilledInvoice ? invoiceData.invoiceReceivedAt : invoiceData.invoiceSentAt;
		return {
			invoiceDeliveryDate: invoiceDeliveryDateString ? new Date(invoiceDeliveryDateString) : null,
			invoicePaidAt: invoiceData.invoicePaidAt ? new Date(invoiceData.invoicePaidAt) : null,
			paymentTargetDays: invoiceData.paymentTargetDays ?? DEFAULT_PAYMENT_TARGET_DAYS,
		};
	}, [invoiceData.invoiceReceivedAt, invoiceData.invoiceSentAt, invoiceData.invoicePaidAt, invoiceData.paymentTargetDays, isSelfBilledInvoice]);

	const {
		handleSubmit,
		control,
		formState: { isDirty, isSubmitted, isValid },
		watch,
		trigger,
	} = useForm<FormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<FormData>(schema),
		mode: "onChange",
	});

	const invoiceDeliveryDateValue = watch("invoiceDeliveryDate");

	useEffect(() => {
		trigger("invoicePaidAt");
	}, [invoiceDeliveryDateValue, trigger, isSubmitted]);

	const formIsSubmittable = useFormIsSubmittable({
		isSubmitted,
		isDirty,
		isValid,
		isLoading: isBusy,
		enableInitially: !invoiceData.paymentTargetDays,
	});

	const onSubmit = useCallback(async (data: FormData) => {
		try {
			setIsBusy(true);
			await updateInvoicePaymentDetails({
				invoiceId: invoiceData.id, updateData: {
					invoicePaidAt: data.invoicePaidAt ? formatDateToYYYYMMDD(data.invoicePaidAt) : null,
					invoiceDeliveryDate: data.invoiceDeliveryDate ? formatDateToYYYYMMDD(data.invoiceDeliveryDate) : null,
					paymentTargetDays: data.paymentTargetDays,
				},
			});
			onSuccess();
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
		}
	}, [invoiceData.id, onSuccess]);

	const deliveryDateLabel = isSelfBilledInvoice ? "Rechnung erhalten am" : "Rechnung versendet am";
	const deliveryDateText = isSelfBilledInvoice ? "Rechnungseingangsdatum" : "Versanddatum";
	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Zahlungsdetails bearbeiten</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Update failed">{serverErrorMessage}</SidebarErrorOverlay>}

				{isAlreadyProcessed && (
					<>
						<Hint theme="warning"
							  size="sm">
							<p>Diese Rechnung wurde
								bereits {currentStatusId === invoiceStatusId.Paid ? "bezahlt" : currentStatusId === invoiceStatusId.Reversed ? "storniert" : "versendet"} und
								sollte daher nur bearbeitet werden, falls die Informationen an das verschickte
								Rechnungsdokument
								angepasst werden müssen.</p>
							<p>Bitte beachte, dass die Bearbeitung von bereits versendeten oder
								bezahlten Rechnungen zu Unstimmigkeiten führen kann.
							</p>
						</Hint>
						<div className="h-2" />
					</>

				)}

				<FormSectionGroup>
					<FormSection title="Zahlungsziel">
						<div className="flex w-24 items-center gap-x-1">
							<Input
								textAlign="center"
								control={control}
								name="paymentTargetDays"
								type="number"
								placeholder="30"
							/> Tage
						</div>
					</FormSection>

					<FormSection title={deliveryDateLabel}>
						{!deliveryDateDisabled && <DatePickerInput
							control={control}
							name="invoiceDeliveryDate"

						/>}
						{deliveryDateDisabled && (
							<Hint theme="info"
								  size="sm">
								Das {deliveryDateText} kann erst bearbeitet werden, wenn der Rechnungsstatus
								auf &quot;Versendet&quot; oder höher aktualisiert wurde.
							</Hint>
						)}
					</FormSection>

					<FormSection title="Zahlungseingang am">
						{!paidAtDisabled && <DatePickerInput
							control={control}
							name="invoicePaidAt"
						/>}
						{paidAtDisabled && (
							<Hint theme="info"
								  size="sm">
								Das Zahlungseingangsdatum kann erst bearbeitet werden, wenn der Rechnungsstatus
								auf &quot;Bezahlt&quot; aktualisiert wurde.
							</Hint>
						)}
					</FormSection>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable}>
					{t("form:buttonTexts.save")}
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					{t("form:buttonTexts.cancel")}
				</Button>
			</SidebarFooter>
		</form>
	);
};

export default UpdateInvoicePaymentInformationForm;